import { useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, Building2, FileText, Image as ImageIcon, Save, Trash2 } from "lucide-react";

const FIELDS = [
  { key: "company_name", label: "Firma Adı", ph: "ABC Sanayi ve Tic. Ltd. Şti." },
  { key: "phone", label: "Telefon", ph: "+90 212 000 00 00" },
  { key: "email", label: "E-posta", ph: "info@firma.com" },
  { key: "tax_office", label: "Vergi Dairesi", ph: "Merkez V.D." },
  { key: "tax_no", label: "Vergi / VKN No", ph: "1234567890" },
  { key: "website", label: "Web Sitesi", ph: "www.firma.com" },
];

const EMPTY = {
  company_name: "", address: "", phone: "", email: "",
  tax_office: "", tax_no: "", website: "", order_terms: "", logo_path: "",
};

export default function SettingsPage() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("access_token");

  useEffect(() => {
    let alive = true;
    api.get("/settings/company")
      .then(({ data }) => { if (alive) setForm((f) => ({ ...f, ...data })); })
      .catch(() => toast.error("Ayarlar yüklenemedi"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Lütfen bir görsel dosyası seçin"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_path", data.path);
      toast.success("Logo yüklendi. Kaydetmeyi unutmayın.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Logo yüklenemedi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", form);
      setForm((f) => ({ ...f, ...data }));
      toast.success("Firma bilgileri kaydedildi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor...
      </div>
    );
  }

  const logoUrl = form.logo_path ? `${API}/files/${form.logo_path}?auth=${token}` : "";

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ayarlar</h1>
        <p className="text-sm text-slate-500 mt-1">
          {`Firma bilgileri, logo ve sipariş şartları — oluşturulan PDF'lerin üst ve alt kısmında görünür.`}
        </p>
      </div>

      {/* Logo */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-4 w-4 text-slate-500" />
          <h2 className="font-semibold text-slate-800">Logo</h2>
        </div>
        <div className="flex items-center gap-5">
          <div
            className="h-20 w-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0"
            data-testid="logo-preview"
          >
            {logoUrl
              ? <img src={logoUrl} alt="Firma logosu" className="max-h-full max-w-full object-contain" />
              : <span className="text-xs text-slate-400">Logo yok</span>}
          </div>
          <div className="flex flex-col gap-2 items-start">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogoPick} data-testid="logo-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Logo Yükle
            </Button>
            {form.logo_path && (
              <Button type="button" variant="ghost" className="text-rose-600 hover:text-rose-700 h-8 px-2" onClick={() => set("logo_path", "")} data-testid="logo-remove-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Kaldır
              </Button>
            )}
            <p className="text-xs text-slate-400">{`PNG/JPG, en fazla 8 MB. PDF'in sol üst köşesinde görünür.`}</p>
          </div>
        </div>
      </section>

      {/* Firma bilgileri */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-4 w-4 text-slate-500" />
          <h2 className="font-semibold text-slate-800">Firma Bilgileri</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((fld) => (
            <div key={fld.key} className={fld.key === "company_name" ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-semibold text-slate-600">{fld.label}</Label>
              <Input
                data-testid={`field-${fld.key}`}
                value={form[fld.key] || ""}
                onChange={(e) => set(fld.key, e.target.value)}
                placeholder={fld.ph}
                className="mt-1.5"
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Adres</Label>
            <textarea
              data-testid="field-address"
              value={form.address || ""}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Firma adresi..."
              rows={2}
              className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
        </div>
      </section>

      {/* Sipariş şartları */}
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <FileText className="h-4 w-4 text-slate-500" />
          <h2 className="font-semibold text-slate-800">Sipariş Şartları</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          {`Örn. ödeme, teslimat ve iade koşulları — Sipariş PDF'inin en altında "Sipariş Şartları" bölümünde görünür.`}
        </p>
        <textarea
          data-testid="field-order_terms"
          value={form.order_terms || ""}
          onChange={(e) => set("order_terms", e.target.value)}
          placeholder={"• Ödeme: 30 gün vadeli\n• Teslimat: 5 iş günü\n• Fiyatlara KDV dahil değildir"}
          rows={5}
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
      </section>

      <div className="flex justify-end sticky bottom-4">
        <Button onClick={save} disabled={saving} data-testid="settings-save-btn" className="bg-slate-900 hover:bg-slate-800 h-11 px-6 shadow-lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Kaydet
        </Button>
      </div>
    </div>
  );
}
